/*
 * Pure model for the Personal Information form: client -> form values,
 * dirty check, values -> update payload. Mailing mirrors physical when
 * "same". The SSN is never in the client record (only its last 4); the form
 * holds it only after an audited reveal or when the user types a new one.
 */
import { z } from "zod";

import {
  addressSchema,
  dateString,
  optionalEmail,
  optionalPhone,
  optionalText
} from "@/lib/forms/schema";

import type {
  ForeignNational,
  UpdateForeignNationalRequest
} from "../../../../api/types/foreignNational";
import {
  addressFromRaw,
  addressToPayload,
  addressesEqual,
  isAddressEmpty,
  normalizeAddress,
  type AddressValues
} from "../../lib/address";
import {
  dateInputToIso,
  normalizeText,
  toDateInputValue
} from "../../lib/format";

export type { AddressValues } from "../../lib/address";

export type PersonalValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  otherNames: string;
  nativeName: string;
  title: string;
  pronouns: string;
  sex: string;
  maritalStatus: string;
  birthday: string;
  cityOfBirth: string;
  stateOfBirth: string;
  countryOfBirth: string;
  /** First entry is the primary citizenship. */
  citizenships: string[];
  email: string;
  phone: string;
  secondaryEmail: string;
  secondaryPhone: string;
  preferredLanguage: string;
  interpreterNeeded: boolean;
  aNumber: string;
  uscisAccountNumber: string;
  ssn: string;
  currentImmigrationStatus: string;
  immigrationStatusExpiration: string;
  durationOfStatus: boolean;
  priorityDate: string;
  preferenceCategory: string;
  eadNumber: string;
  eadExpiration: string;
  advanceParoleExpiration: string;
  visaNumber: string;
  visaExpiration: string;
  greenCardExpiration: string;
  programEndDate: string;
  h1bMaxOutDate: string;
  physical: AddressValues;
  mailing: AddressValues;
  mailingSameAsPhysical: boolean;
  foreign: AddressValues;
  inCareOf: string;
};

export const TITLE_OPTIONS = ["Mr.", "Ms.", "Mrs.", "Mx.", "Dr."] as const;
export const PRONOUN_OPTIONS = ["she/her", "he/him", "they/them"] as const;

export const SEX_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "x", label: "X (another gender identity)" }
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "single", label: "Single, never married" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
  { value: "widowed", label: "Widowed" },
  { value: "separated", label: "Legally separated" },
  { value: "annulled", label: "Marriage annulled" }
];

/** The portal and intake are translated into these. */
export const PORTAL_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish · Español" },
  { value: "pt", label: "Portuguese · Português" },
  { value: "zh-Hans", label: "Chinese (Simplified) · 中文" },
  { value: "hi", label: "Hindi · हिन्दी" },
  { value: "fr", label: "French · Français" },
  { value: "ar", label: "Arabic · العربية" }
];

const OTHER_LANGUAGE_OPTIONS = [
  { value: "am", label: "Amharic" },
  { value: "bn", label: "Bengali" },
  { value: "fa", label: "Farsi" },
  { value: "de", label: "German" },
  { value: "ht", label: "Haitian Creole" },
  { value: "it", label: "Italian" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "pa", label: "Punjabi" },
  { value: "ru", label: "Russian" },
  { value: "so", label: "Somali" },
  { value: "sw", label: "Swahili" },
  { value: "tl", label: "Tagalog" },
  { value: "tr", label: "Turkish" },
  { value: "uk", label: "Ukrainian" },
  { value: "ur", label: "Urdu" },
  { value: "vi", label: "Vietnamese" },
  { value: "yo", label: "Yoruba" }
];

export const LANGUAGE_GROUPS = [
  { label: "Portal available in", options: PORTAL_LANGUAGE_OPTIONS },
  { label: "Other languages", options: OTHER_LANGUAGE_OPTIONS }
];

export const languageLabel = (code?: string | null): string | null => {
  if (!code) return null;
  const all = [...PORTAL_LANGUAGE_OPTIONS, ...OTHER_LANGUAGE_OPTIONS];
  return all.find((o) => o.value === code)?.label.split(" · ")[0] ?? code;
};

const digits = (v: string) => v.replace(/\D/g, "");

/** Everything optional; formats are checked so bad data never saves silently. */
export const personalSchema: z.ZodType<PersonalValues, PersonalValues> =
  z.object({
    firstName: optionalText(),
    middleName: optionalText(),
    lastName: optionalText(),
    otherNames: optionalText(),
    nativeName: optionalText(),
    title: optionalText(),
    pronouns: optionalText(),
    sex: optionalText(),
    maritalStatus: optionalText(),
    birthday: dateString(),
    cityOfBirth: optionalText(),
    stateOfBirth: optionalText(),
    countryOfBirth: optionalText(),
    citizenships: z.array(z.string()),
    email: optionalEmail(),
    phone: optionalPhone(),
    secondaryEmail: optionalEmail(),
    secondaryPhone: optionalPhone(),
    preferredLanguage: optionalText(),
    interpreterNeeded: z.boolean(),
    aNumber: z
      .string()
      .refine(
        (v) => !v.trim() || /^\d{7,9}$/.test(digits(v)),
        "An A-Number has 7 to 9 digits (A012345678)."
      ),
    uscisAccountNumber: z
      .string()
      .refine(
        (v) => !v.trim() || /^\d{12}$/.test(digits(v)),
        "A USCIS online account number has 12 digits."
      ),
    ssn: z
      .string()
      .refine(
        (v) => !v.trim() || digits(v).length === 9,
        "A Social Security number has 9 digits."
      ),
    currentImmigrationStatus: optionalText(),
    immigrationStatusExpiration: dateString(),
    durationOfStatus: z.boolean(),
    priorityDate: dateString(),
    preferenceCategory: optionalText(),
    eadNumber: optionalText(),
    eadExpiration: dateString(),
    advanceParoleExpiration: dateString(),
    visaNumber: optionalText(),
    visaExpiration: dateString(),
    greenCardExpiration: dateString(),
    programEndDate: dateString(),
    h1bMaxOutDate: dateString(),
    physical: addressSchema(),
    mailing: addressSchema(),
    mailingSameAsPhysical: z.boolean(),
    foreign: addressSchema(),
    inCareOf: optionalText()
  });

const citizenshipsOf = (fn: ForeignNational | null | undefined): string[] => {
  const list = (fn?.citizenships ?? []).map(normalizeText).filter(Boolean);
  if (list.length) return list;
  const single = normalizeText(fn?.country_of_citizenship);
  return single ? [single] : [];
};

export const valuesFromClient = (
  fn: ForeignNational | null | undefined
): PersonalValues => {
  const physical = addressFromRaw(fn?.physical_address);
  const mailing = addressFromRaw(fn?.mailing_address);
  const same = isAddressEmpty(mailing) || addressesEqual(physical, mailing);
  return {
    firstName: normalizeText(fn?.first_name),
    middleName: normalizeText(fn?.middle_name),
    lastName: normalizeText(fn?.last_name),
    otherNames: (fn?.other_names ?? [])
      .map(normalizeText)
      .filter(Boolean)
      .join(", "),
    nativeName: normalizeText(fn?.native_name),
    title: normalizeText(fn?.title),
    pronouns: normalizeText(fn?.pronouns),
    sex: normalizeText(fn?.sex),
    maritalStatus: normalizeText(fn?.marital_status),
    birthday: toDateInputValue(fn?.birthday),
    cityOfBirth: normalizeText(fn?.city_of_birth),
    stateOfBirth: normalizeText(fn?.state_of_birth),
    countryOfBirth: normalizeText(fn?.country_of_birth),
    citizenships: citizenshipsOf(fn),
    email: normalizeText(fn?.email),
    phone: normalizeText(fn?.phone),
    secondaryEmail: normalizeText(fn?.secondary_email),
    secondaryPhone: normalizeText(fn?.secondary_phone),
    preferredLanguage: normalizeText(fn?.preferred_language),
    interpreterNeeded: Boolean(fn?.interpreter_needed),
    aNumber: normalizeText(fn?.a_number),
    uscisAccountNumber: normalizeText(fn?.uscis_account_number),
    ssn: "",
    currentImmigrationStatus: normalizeText(fn?.current_immigration_status),
    immigrationStatusExpiration: toDateInputValue(
      fn?.immigration_status_expiration
    ),
    durationOfStatus: Boolean(fn?.duration_of_status),
    priorityDate: toDateInputValue(fn?.priority_date),
    preferenceCategory: normalizeText(fn?.preference_category),
    eadNumber: normalizeText(fn?.ead_number),
    eadExpiration: toDateInputValue(fn?.ead_expiration),
    advanceParoleExpiration: toDateInputValue(fn?.advance_parole_expiration),
    visaNumber: normalizeText(fn?.visa_number),
    visaExpiration: toDateInputValue(fn?.visa_expiration),
    greenCardExpiration: toDateInputValue(fn?.green_card_expiration),
    programEndDate: toDateInputValue(fn?.program_end_date),
    h1bMaxOutDate: toDateInputValue(fn?.h1b_max_out_date),
    physical,
    mailing: same ? physical : mailing,
    mailingSameAsPhysical: same,
    foreign: addressFromRaw(fn?.foreign_address),
    inCareOf: normalizeText(fn?.in_care_of)
  };
};

/** The effective mailing address, honoring the "same as physical" toggle. */
export const effectiveMailing = (v: PersonalValues): AddressValues =>
  v.mailingSameAsPhysical ? v.physical : v.mailing;

const comparable = (val: unknown): unknown => {
  if (typeof val === "string") return normalizeText(val);
  if (Array.isArray(val)) return val.map(normalizeText).filter(Boolean);
  return val;
};

/** Whitespace-insensitive snapshot used for dirty tracking. */
export const snapshotOf = (v: PersonalValues): string => {
  const {
    physical,
    mailing: _mailing,
    foreign,
    mailingSameAsPhysical,
    ...rest
  } = v;
  const scalars = Object.fromEntries(
    Object.entries(rest).map(([k, val]) => [k, comparable(val)])
  );
  return JSON.stringify({
    ...scalars,
    physical: normalizeAddress(physical),
    mailing: normalizeAddress(effectiveMailing(v)),
    foreign: normalizeAddress(foreign),
    mailingSameAsPhysical
  });
};

export const isPersonalDirty = (
  values: PersonalValues,
  saved: PersonalValues
): boolean => snapshotOf(values) !== snapshotOf(saved);

const orNull = (s: string): string | null => normalizeText(s) || null;

/** Comma- or semicolon-separated names to a list ("Amara Okafor-Bello, Amy Okafor"). */
export const splitNames = (s: string): string[] =>
  s
    .split(/[;,]/)
    .map((n) => n.trim())
    .filter(Boolean);

/**
 * The update for every field on the form. `ssn` is left out: the hook adds
 * it only when the user changed it, since the form never holds the stored one.
 */
export const buildPersonalPayload = (
  v: PersonalValues
): UpdateForeignNationalRequest => {
  const physical = addressToPayload(v.physical);
  const mailing = v.mailingSameAsPhysical
    ? physical
    : addressToPayload(v.mailing);
  const citizenships = [
    ...new Set(v.citizenships.map(normalizeText).filter(Boolean))
  ];
  return {
    first_name: orNull(v.firstName),
    middle_name: orNull(v.middleName),
    last_name: orNull(v.lastName),
    other_names: splitNames(v.otherNames),
    native_name: orNull(v.nativeName),
    title: orNull(v.title),
    pronouns: orNull(v.pronouns),
    sex: orNull(v.sex),
    marital_status: orNull(v.maritalStatus),
    birthday: dateInputToIso(v.birthday),
    city_of_birth: orNull(v.cityOfBirth),
    state_of_birth: orNull(v.stateOfBirth),
    country_of_birth: orNull(v.countryOfBirth),
    citizenships,
    email: orNull(v.email),
    phone: orNull(v.phone),
    secondary_email: orNull(v.secondaryEmail),
    secondary_phone: orNull(v.secondaryPhone),
    preferred_language: orNull(v.preferredLanguage),
    interpreter_needed: v.interpreterNeeded,
    a_number: orNull(v.aNumber),
    uscis_account_number: orNull(digits(v.uscisAccountNumber)),
    current_immigration_status: orNull(v.currentImmigrationStatus),
    immigration_status_expiration: v.durationOfStatus
      ? null
      : dateInputToIso(v.immigrationStatusExpiration),
    duration_of_status: v.durationOfStatus,
    priority_date: dateInputToIso(v.priorityDate),
    preference_category: orNull(v.preferenceCategory),
    ead_number: orNull(v.eadNumber),
    ead_expiration: dateInputToIso(v.eadExpiration),
    advance_parole_expiration: dateInputToIso(v.advanceParoleExpiration),
    visa_number: orNull(v.visaNumber),
    visa_expiration: dateInputToIso(v.visaExpiration),
    green_card_expiration: dateInputToIso(v.greenCardExpiration),
    program_end_date: dateInputToIso(v.programEndDate),
    h1b_max_out_date: dateInputToIso(v.h1bMaxOutDate),
    physical_address: physical,
    mailing_address: mailing,
    foreign_address: addressToPayload(v.foreign),
    in_care_of: orNull(v.inCareOf)
  };
};

/** Profile field (API name) for each form field, for source tags. */
export const SOURCE_FIELD: Partial<Record<keyof PersonalValues, string>> = {
  firstName: "first_name",
  middleName: "middle_name",
  lastName: "last_name",
  otherNames: "other_names",
  nativeName: "native_name",
  sex: "sex",
  maritalStatus: "marital_status",
  birthday: "birthday",
  cityOfBirth: "city_of_birth",
  stateOfBirth: "state_of_birth",
  countryOfBirth: "country_of_birth",
  citizenships: "citizenships",
  aNumber: "a_number",
  uscisAccountNumber: "uscis_account_number",
  ssn: "ssn",
  currentImmigrationStatus: "current_immigration_status",
  immigrationStatusExpiration: "immigration_status_expiration",
  eadNumber: "ead_number",
  eadExpiration: "ead_expiration",
  advanceParoleExpiration: "advance_parole_expiration",
  visaNumber: "visa_number",
  visaExpiration: "visa_expiration",
  greenCardExpiration: "green_card_expiration",
  programEndDate: "program_end_date",
  h1bMaxOutDate: "h1b_max_out_date",
  priorityDate: "priority_date"
};

/** DOM ids the rest of the profile deep-links to. */
export const PERSONAL_SECTION_IDS = {
  biographical: "personal-biographical",
  contact: "personal-contact-info",
  identifiers: "personal-identifiers",
  immigration: "personal-immigration-information",
  expirations: "personal-expiration-dates",
  address: "personal-current-address"
} as const;
